import { toLegacyDetail, v1Get } from '@/services/v1Api';

/**
 * Warehouse Projects API Service
 * Provides specialized endpoints for warehouse operations
 */

/**
 * Get warehouse projects based on view type and filters
 * @param {Object} params - Query parameters
 * @param {string} params.view - Warehouse view type (today_in, today_out, next_3_days, etc.)
 * @param {string} params.date - Base date for calculations (YYYY-MM-DD format)
 * @param {string} params.search - Search projects by name or description
 * @param {string} params.status - Filter by specific status name
 * @param {string} params.start_date - Custom start date (for custom_range view)
 * @param {string} params.end_date - Custom end date (for custom_range view)
 * @returns {Promise} API response with warehouse projects
 */
export const getWarehouseProjects = async (params = {}) => {
    try {
        const { data } = await v1Get('/api/v1/warehouse/projects', params);
        return toLegacyDetail(data);
    } catch (error) {
        console.error('Error fetching warehouse projects:', error);
        throw error;
    }
};

/**
 * Get today's incoming projects (equipment coming back to warehouse)
 * @param {string} date - Base date (defaults to today)
 * @param {string} search - Search term
 * @returns {Promise} API response
 */
export const getTodayIncomingProjects = async (date = null, search = '') => {
    const params = {
        view: 'today_in',
        ...(date && { date }),
        ...(search && { search })
    };
    return getWarehouseProjects(params);
};

/**
 * Get today's outgoing projects (equipment going out of warehouse)
 * @param {string} date - Base date (defaults to today)
 * @param {string} search - Search term
 * @returns {Promise} API response
 */
export const getTodayOutgoingProjects = async (date = null, search = '') => {
    const params = {
        view: 'today_out',
        ...(date && { date }),
        ...(search && { search })
    };
    return getWarehouseProjects(params);
};

/**
 * Get projects for the next 3 days
 * @param {string} date - Base date (defaults to today)
 * @param {string} search - Search term
 * @returns {Promise} API response
 */
export const getNext3DaysProjects = async (date = null, search = '') => {
    const params = {
        view: 'next_3_days',
        ...(date && { date }),
        ...(search && { search })
    };
    return getWarehouseProjects(params);
};

/**
 * Get projects for the next 7 days
 * @param {string} date - Base date (defaults to today)
 * @param {string} search - Search term
 * @returns {Promise} API response
 */
export const getNext7DaysProjects = async (date = null, search = '') => {
    const params = {
        view: 'next_7_days',
        ...(date && { date }),
        ...(search && { search })
    };
    return getWarehouseProjects(params);
};

/**
 * Get projects for the next 30 days (month view)
 * @param {string} date - Base date (defaults to today)
 * @param {string} search - Search term
 * @returns {Promise} API response
 */
export const getNext30DaysProjects = async (date = null, search = '') => {
    const params = {
        view: 'next_30_days',
        ...(date && { date }),
        ...(search && { search })
    };
    return getWarehouseProjects(params);
};

/**
 * Get projects for a custom date range
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @param {string} search - Search term
 * @returns {Promise} API response
 */
export const getCustomRangeProjects = async (startDate, endDate, search = '') => {
    const params = {
        view: 'custom_range',
        start_date: startDate,
        end_date: endDate,
        ...(search && { search })
    };
    return getWarehouseProjects(params);
};

/**
 * Transform API project data to DayColumn format
 * The API returns projects with embedded equipment_lists arrays
 * @param {Object} apiProject - Project from API response (from /api/v1/warehouse/projects)
 * @returns {Array} Array of transformed equipment lists for DayColumn component
 */
export const transformApiProjectToDayColumn = (apiProject) => {
    // Extract time from project start_date
    let time = '00:00';
    if (apiProject.start_date) {
        const startDate = new Date(apiProject.start_date);
        if (!isNaN(startDate.getTime())) {
            time = startDate.toLocaleTimeString('en-GB', { 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: false 
            });
        }
    }

    // Transform each equipment list in the project
    return apiProject.equipment_lists.map(equipmentList => ({
        // Equipment list IDs
        id: equipmentList.id, // Equipment list ID
        equipmentListId: equipmentList.id,
        projectId: apiProject.id, // Project ID from the parent project
        
        // Display information
        status: apiProject.status?.global_status?.name || 'unknown',
        time: time,
        startDate: apiProject.start_date,
        projectNumber: apiProject.project_number,
        projectName: equipmentList.name, // Equipment list name
        projectManager: apiProject.project_manager?.display_name || apiProject.creator?.display_name || 'Unassigned',
        totalItems: 0, // Will be populated by scan summary API
        totalPrepped: 0, // Will be populated by scan summary API
        client: apiProject.client?.company_name || 'Client Name',
        type: apiProject.type?.name || 'Equipment List',
        description: equipmentList.description,
        venue: apiProject.venue,
        typeInfo: apiProject.type,
        statusInfo: apiProject.status,
        
        // Additional data for navigation
        project: apiProject,
        equipmentList: equipmentList
    }));
};

/**
 * Transform API project data to calendar format
 * @param {Object} apiProject - Project from API response
 * @returns {Array} Array of transformed equipment lists for calendar component
 */
export const transformApiProjectToCalendar = (apiProject) => {
    return transformApiProjectToDayColumn(apiProject);
};
